import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { create, keyResolver, windowedFiniteBatchScheduler } from "@yornaath/batshit";
import axios from "axios";
import type { RouterOutputs } from "@remi/api";
import {
  BATSHIT_URL_SAFE_MAX_BATCH_SIZE,
  BATSHIT_WINDOW_MS,
  BROKEN_IMAGE_SRC,
  FIFTY_MINUTES_MS,
  FIVE_MINUTES_MS,
} from "@remi/constants";
import { remiLogger } from "@remi/logging";
import { FileType } from "@remi/types/file";
import { DEFAULT_MIME_TYPE, getMimeTypeFromFilename } from "@remi/types/mimetype";

import { api, proxyApi } from "~/utils/api";
import { fileDownloadClient } from "~/utils/file";
import { getQueryConfig } from "~/utils/query-config";
import { display } from "~/components/ui/sonner";
import { buildMissingFile } from "./buildMissingFile";

// Hoisted to module scope so Math.random() runs once. Per-render calls produce
// a new staleTime value each render; React Query's shallowEqualObjects detects
// the changed value, fires observerOptionsUpdated, and resets the stale
// timeout — causing a refetch loop that cascades into avatar flickering.
const FILE_QUERY_CONFIG = {
  ...getQueryConfig(FIVE_MINUTES_MS),
  refetchInterval: FIFTY_MINUTES_MS,
};

const getFileQueryKey = (id: string | undefined) => {
  return ["batched-file", { id }];
};

const getQuery = (id: string) => ({
  queryKey: getFileQueryKey(id),
  queryFn: () => {
    return fileBatcher.fetch(id);
  },
  enabled: !!id,
  ...FILE_QUERY_CONFIG,
});

export const useFileById = (fileId?: string | null) => {
  return useQuery(getQuery(fileId ?? ""));
};
export type FileById = RouterOutputs["document"]["getFilesByIds"][number];

export const useFilesByIds = (ids: string[]) => {
  const dedupedIds = Array.from(new Set(ids));
  const queries = useQueries({
    queries: dedupedIds.map((id) => getQuery(id)),
  });
  const data = useMemo(() => queries.map((q) => q.data).filter((f) => !!f), [queries]);
  const error = useMemo(() => queries.find((q) => q.error)?.error, [queries]);
  const isPending = useMemo(() => queries.some((q) => q.isPending), [queries]);
  return { data, error, isPending };
};

const fileBatcher = create({
  fetcher: async (ids: string[]): Promise<FileById[]> => {
    const files = await proxyApi.document.getFilesByIds.query(ids);
    const fileSet = new Set(files.map((f) => f.id));
    for (const id of ids) {
      if (!fileSet.has(id)) {
        files.push(buildMissingFile(id));
      }
    }
    return files;
  },
  resolver: keyResolver("id"),
  scheduler: windowedFiniteBatchScheduler({
    windowMs: BATSHIT_WINDOW_MS,
    maxBatchSize: BATSHIT_URL_SAFE_MAX_BATCH_SIZE,
  }),
});
type GetFileParams = {
  url: string;
  name: string;
  type?: FileType;
};

export const downloadFile = async ({ url, name, type }: GetFileParams): Promise<void> => {
  try {
    const file = await getFile({ url, name, type });
    const link = document.createElement("a");
    const objectUrl = URL.createObjectURL(file);
    link.href = objectUrl;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // The blob read starts after the click event is dispatched, so a synchronous revoke races it
    // and can yield a zero-byte save (most reproducible in the Android WebView).
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      remiLogger.captureCounterMetric("file.download.network-error", 1);
      display.error("Failed to download file", {
        description: "Please check your internet connection and try again.",
      });
      return;
    }
    throw error;
  }
};

const getMimeTypeFromFileType = (fileType: FileType) => {
  switch (fileType) {
    case FileType.DOCUMENT:
      return "application/pdf";
    case FileType.PHOTO:
      return "image/jpeg";
    case FileType.AUDIO:
      return "audio/mpeg";
    default:
      return "application/octet-stream";
  }
};

// Video containers vary (mov/webm/avi/…), so stamp the download blob from the filename extension
// and only fall back to video/mp4 when the extension is unrecognized.
const getContentType = (name: string, fileType?: FileType): string => {
  if (fileType === FileType.VIDEO) {
    const fromName = getMimeTypeFromFilename(name);
    return fromName === DEFAULT_MIME_TYPE ? "video/mp4" : fromName;
  }
  return fileType ? getMimeTypeFromFileType(fileType) : getMimeTypeFromFilename(name);
};

export const getFile = async ({ url, name, type: fileType }: GetFileParams): Promise<File> => {
  const res = await fileDownloadClient.get(url, { responseType: "blob" });
  const type = getContentType(name, fileType);
  return new File([res.data], name, { type });
};

export const useFileDelete = () => {
  const invalidate = useInvalidateFileById();
  const utils = api.useUtils();
  return api.document.deleteFile.useMutation({
    async onSettled(_d, _e, variables) {
      await utils.document.byParams.invalidate();
      await invalidate(variables.fileId);
      if (variables.projectIdForOptimisticUpdate) {
        await utils.file.getByProjectId.invalidate({ projectId: variables.projectIdForOptimisticUpdate });
      }
    },
  });
};

export const useInvalidateFileById = () => {
  const client = useQueryClient();
  return useCallback(
    async (fileId?: string) => {
      await client.invalidateQueries({ queryKey: getFileQueryKey(fileId) });
    },
    [client],
  );
};

const MAX_BROKEN_IMAGE_RETRIES = 5;

/**
 * Auto-retry signed-URL fetches that came back as the broken-image sentinel.
 * Capped per-file so we don't spin forever on permanently-missing objects.
 */
export const useRetryBrokenFiles = (files: { id: string; url: string }[]) => {
  const invalidateFile = useInvalidateFileById();
  const retryCountsRef = useRef(new Map<string, number>());
  useEffect(() => {
    const newBrokenIds = files
      .filter((f) => f.url === BROKEN_IMAGE_SRC && (retryCountsRef.current.get(f.id) ?? 0) < MAX_BROKEN_IMAGE_RETRIES)
      .map((f) => f.id);
    if (newBrokenIds.length === 0) {
      return;
    }
    for (const id of newBrokenIds) {
      retryCountsRef.current.set(id, (retryCountsRef.current.get(id) ?? 0) + 1);
    }
    void Promise.all(newBrokenIds.map((id) => invalidateFile(id)));
  }, [files, invalidateFile]);
};
